import React, {useRef, useState} from 'react';
import MaterialTable, {MTableToolbar} from 'material-table';
import {useQuery} from '@apollo/client';
import {TRACTS_TABLE_QUERY} from './tracts/TractQueries';
import LoadingView from '../components/LoadingView';
import {makeStyles} from '@material-ui/styles';

// Helper to map color codes
function getColorStyle(color) {
    switch (color) {
        case 'M&S':
            return {backgroundColor: 'rgba(255,255,0,0.53)', color: 'black'};
        case 'M':
            return {backgroundColor: 'rgba(255,0,0,0.2)', color: 'black'};
        case 'S':
            return {backgroundColor: 'rgba(0,0,255,0.2)', color: 'black'};
        case 'ORRI':
            return {backgroundColor: 'rgb(119, 221, 119)', color: 'black'};
        case 'All':
            return {backgroundColor: 'rgba(0,200,255,0.2)', color: 'black'};
        default:
            return {};
    }
}

const anyStyles = makeStyles(() => ({
    buttons: {
        cursor: 'pointer',
        borderRadius: '15px',
        border: 'none',
        display: 'flex',
        textAlign: 'center',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.4)',
    },
    // Container to fix scrolling properly
    scrollContainer: {
        height: 'calc(100vh - 70px)', // Account for header
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', // Prevent whole page from scrolling
    },
    tableContainer: {
        flex: 1,
        overflow: 'auto', // Allow table to scroll both directions
    },
    // Need to figure out how to override material table gutters on toolbar
    // toolbar: {
    //     '.MuiToolbar-gutters': {
    //         paddingLeft: 0,
    //         marginLeft: 0// Set left padding to 0
    //     },
    // },
}));

// Parse fractional strings like "3/8", "1/4", or "1"
function fractionToNumber(fracStr) {
    if (!fracStr) return 0;
    if (fracStr.includes('/')) {
        const [numerator, denominator] = fracStr.split('/');
        return parseFloat(numerator) / parseFloat(denominator);
    }
    return parseFloat(fracStr);
}

// Identical columns to original, just remove Link for PWA
const LandsColumns = [
    {
        title: '#',
        field: 'rowCount',
    },
    {
        title: 'LAND ID',
        field: 'id',
        render: (rowData) => (
            <span style={getColorStyle(rowData.color)}>
                {rowData.id}
            </span>
        ),
    },
    {title: 'TYPE', field: 'color'},
    {
        title: 'NMA',
        field: 'familyNMA',
        render: (rowData) => parseFloat(rowData.familyNMA).toFixed(8)
    },
    {
        title: 'NSA',
        field: 'familyNSA',
        render: (rowData) => parseFloat(rowData.familyNSA).toFixed(8)
    },
    {
        title: 'MES %',
        field: 'mesPercent',
        render: (rowData) => rowData.mesPercent.toFixed(2)
    },
    {
        title: 'MPI %',
        field: 'mpiPercent',
        render: (rowData) => rowData.mpiPercent.toFixed(2)
    },
    {
        title: 'SRO %',
        field: 'sroPercent',
        render: (rowData) => rowData.sroPercent.toFixed(2)
    }
];

function simple(type) {
    return type ? type : 'ALL'
}

function MobileLandsTable({history}) {
    const classes = anyStyles(undefined);
    const {loading, error, data} = useQuery(TRACTS_TABLE_QUERY);
    const [selectedColor, setSelectedColor] = useState(null); // default filter

    const tableRef = useRef(null);
    const handleSelectChange = (event) => {
        const value = event.target.value;
        console.log(value, 'THIS IS THE VALUE SELECTED')
        switch (value) {
            case 'ALL':
                setSelectedColor(null);
                break;
            default:
                setSelectedColor(value);
                break;
        }
    };

    if (error) return <h1>Can't Load Tracts!</h1>;
    if (loading) return <LoadingView/>;

    const allTracts = data.Tract;
    const countAll = allTracts.length;
    const countMS = allTracts.filter(t => t.color === 'M&S').length;
    const countS = allTracts.filter(t => t.color === 'S').length;
    const countM = allTracts.filter(t => t.color === 'M').length;
    const countORRI = allTracts.filter(t => t.color === 'ORRI').length;

    // Mapping tract id to static row number based on original tract indexes
    const rowNumberMap = new Map();
    allTracts.forEach((tract, index) => {
        rowNumberMap.set(tract.id, index + 1);
    });

    // 1) Filter the top-level array by color (M, S, M&S, or ORRI)
    const filteredTracts = selectedColor
        ? data.Tract.filter(t => t.color === selectedColor)
        : data.Tract;

    const tableData = filteredTracts.map((tract, index) => {
        // Aggregate mineral fractions by owner type
        const mesTotal = tract.mineralsOwned
            .filter(o => o.Owner.name === 'MES')
            .reduce((sum, o) => sum + fractionToNumber(o.mineralFraction), 0);

        const mpiTotal = tract.mineralsOwned
            .filter(o => o.Owner.name === 'MPI')
            .reduce((sum, o) => sum + fractionToNumber(o.mineralFraction), 0);

        const sroTotal = tract.mineralsOwned
            .filter(o => o.Owner.name === 'SRO')
            .reduce((sum, o) => sum + fractionToNumber(o.mineralFraction), 0);

        return {
            rowCount: rowNumberMap.get(tract.id), // Static row number map from tract original index
            id: tract.id,
            color: tract.color,
            familyNMA: tract.familyNMA,
            familyNSA: tract.familyNSA,
            grossMineralAcres: tract.grossMineralAcres,
            grossSurfaceAcres: tract.grossSurfaceAcres,
            mesPercent: mesTotal * 100,
            mpiPercent: mpiTotal * 100,
            sroPercent: sroTotal * 100
        };
    });

    return (
        <div className={classes.scrollContainer}>
            <div className={classes.tableContainer}>
                <div style={{display: 'flex', flexDirection: 'row'}}>
                    <div style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                        <MaterialTable
                            tableRef={tableRef}
                            data={tableData}
                            columns={LandsColumns}
                            title=''
                            options={{
                                tableLayout: 'auto',
                                paging: false,
                                sorting: true,
                                exportButton: false, // No export on PWA
                                exportAllData: false, // No export on PWA
                                search: true,
                                searchFieldAlignment: 'left',
                                searchFieldStyle: {
                                    border: 'none',
                                    paddingTop: '4px',
                                    paddingBottom: '4px',
                                    paddingLeft: '1px',
                                    paddingRight: '1px',
                                    borderRadius: '4px',
                                    backgroundColor: '#fff',
                                    color: '#000',
                                },
                                headerStyle: {
                                    backgroundColor: '#1976d2',
                                    color: '#FFF',
                                    textAlign: 'left',
                                    justifyContent: 'center',
                                    whiteSpace: 'pre',
                                    position: 'sticky',
                                    top: 0,
                                },
                                maxBodyHeight: 'calc(100vh - 140px)',
                                toolbarButtonAlignment: 'left',
                            }}
                            components={{
                                Toolbar: (props) => (
                                    <div
                                        style={{
                                            backgroundColor: '#1976d2',
                                            display: 'flex',
                                            // justifyContent: 'space-between',
                                            alignItems: 'center',
                                            paddingLeft: '0px'
                                        }}
                                    >
                                        <div className={classes.toolbar}>

                                            <MTableToolbar {...props} />
                                        </div>
                                        <select
                                            onChange={handleSelectChange}
                                            value={simple(selectedColor)}
                                            style={{padding: '0.25rem', minWidth: '100px'}}
                                            className={`${classes.buttons}`}
                                        >
                                            <option value='ALL'>ALL ({countAll})</option>
                                            <option value='M&S'>M&S ({countMS})</option>
                                            <option value='S'>S ({countS})</option>
                                            <option value='M'>M ({countM})</option>
                                            <option value='ORRI'>ORRI ({countORRI})</option>
                                        </select>
                                        {/* Removed Add Land button for PWA */}
                                    </div>
                                ),
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MobileLandsTable;